import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { motion } from 'framer-motion';
import AuthService from '../../services/AuthService';

const NMPLogo = require('../../Images/NMP.png');
const SkyAngelLogo = require('../../Images/skyangel.png');

interface LoginFormProps {
  onLoginSuccess: () => void;
  onSwitchToRegister: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess, onSwitchToRegister }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await AuthService.login(formData.email, formData.password);
      
      if (response.success) {
        onLoginSuccess();
      } else {
        setError(response.message || 'Error al iniciar sesión');
      }
    } catch (error) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Crear partículas flotantes
  useEffect(() => {
    const createParticles = () => {
      const particlesContainer = document.getElementById('particles');
      if (!particlesContainer) return;
      
      const particleCount = 50;
      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement('div');
        particle.className = 'particle';
        particle.style.left = Math.random() * 100 + '%';
        particle.style.animationDelay = Math.random() * 8 + 's';
        particle.style.animationDuration = (Math.random() * 3 + 5) + 's';
        particlesContainer.appendChild(particle);
      }
    };

    createParticles();
  }, []);

  return (
    <LoginPageContainer>
      {/* Animaciones de fondo */}
      <BackgroundAnimation>
        <FloatingShape className="shape-1" />
        <FloatingShape className="shape-2" />
        <FloatingShape className="shape-3" />
        <FloatingShape className="shape-4" />
        <ParticlesContainer id="particles" />
      </BackgroundAnimation>

      <FormContainer
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 1, ease: "easeOut" }}
      >
        <LogoSection
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.3 }}
        >
          <LogoContainer>
            <img src={NMPLogo} alt="Nacional Monte de Piedad" style={{ width: '70px', height: '70px' }} />
          </LogoContainer>
          <LogoText>Nacional Monte de Piedad</LogoText>
          <LogoSubtitle>Inteligencia Artifical Piedad</LogoSubtitle>
          <img src={SkyAngelLogo} alt="SkyAngel" style={{ width: '80px', height: '110px', margin: '10px auto', objectFit: 'contain', display: 'block' }} />
        </LogoSection>

        <Form onSubmit={handleSubmit}>
          <FormGroup
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            <InputWrapper>
              <FormInput
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder=" "
                required
              />
              <FloatingLabel>Correo</FloatingLabel>
            </InputWrapper>
          </FormGroup>

          <FormGroup
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.7 }}
          >
            <InputWrapper>
              <FormInput
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder=" "
                required
              />
              <FloatingLabel>Contraseña</FloatingLabel>
            </InputWrapper>
          </FormGroup>

          {error && (
            <ErrorMessage
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              {error}
            </ErrorMessage>
          )}

          <FormGroup
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9 }}
          >
            <LoginButton
              type="submit"
              disabled={loading}
              $loading={loading}
              whileHover={{ y: -3, boxShadow: "0 15px 35px rgba(152, 1, 46, 0.4)" }}
              whileTap={{ y: -1 }}
            >
              {loading ? '' : 'Iniciar Sesión'}
            </LoginButton>
          </FormGroup>
        </Form>
      </FormContainer>
    </LoginPageContainer>
  );
};

// Keyframes para animaciones
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(180deg); }
`;

const particleFloat = keyframes`
  0% {
    transform: translateY(100vh) translateX(0);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(-100px) translateX(100px);
    opacity: 0;
  }
`;

const slideIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(50px) scale(0.9);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const logoSpin = keyframes`
  0% { transform: rotateY(0deg); }
  50% { transform: rotateY(180deg); }
  100% { transform: rotateY(360deg); }
`;

const shine = keyframes`
  0% { left: -100%; }
  100% { left: 100%; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Styled Components
const LoginPageContainer = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  background: linear-gradient(135deg, #98012E 0%, #CA7C8E 50%, #E9C9CE 100%);
`;

const BackgroundAnimation = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
`;

const FloatingShape = styled.div`
  position: absolute;
  opacity: 0.1;
  animation: ${float} 6s ease-in-out infinite;

  &.shape-1 {
    width: 80px;
    height: 80px;
    background: #ffffff;
    border-radius: 50%;
    top: 20%;
    left: 10%;
    animation-delay: 0s;
  }

  &.shape-2 {
    width: 60px;
    height: 60px;
    background: #000000;
    transform: rotate(45deg);
    top: 60%;
    right: 15%;
    animation-delay: 2s;
  }

  &.shape-3 {
    width: 100px;
    height: 100px;
    background: #686869;
    clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
    top: 10%;
    right: 20%;
    animation-delay: 4s;
  }

  &.shape-4 {
    width: 70px;
    height: 70px;
    background: #ffffff;
    border-radius: 20%;
    bottom: 20%;
    left: 20%;
    animation-delay: 1s;
  }
`;

const ParticlesContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;

  .particle {
    position: absolute;
    width: 4px;
    height: 4px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    animation: ${particleFloat} 8s linear infinite;
  }
`;

const FormContainer = styled(motion.div)`
  background: linear-gradient(135deg, #98012E, #CA7C8E);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 25px 50px rgba(152, 1, 46, 0.3);
  width: 100%;
  max-width: 420px;
  position: relative;
  z-index: 10;
  border: 1px solid rgba(255, 255, 255, 0.2);

  @media (max-width: 480px) {
    margin: 20px;
    padding: 30px 25px;
  }
`;

const LogoSection = styled(motion.div)`
  text-align: center;
  margin-bottom: 30px;
`;

const LogosContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
`;

const LogoContainer = styled.div`
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${logoSpin} 2s ease-in-out;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(152,1,46,0.3), transparent);
    animation: ${shine} 3s infinite;
  }
`;

const LogoText = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #ffffff;
  margin-bottom: 5px;
  letter-spacing: -0.5px;

  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const LogoSubtitle = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
`;

const Tagline = styled.div`
  background: rgba(255, 255, 255, 0.9);
  color: #98012E;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  margin-top: 10px;
  display: inline-block;
  animation: ${pulse} 2s infinite;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled(motion.div)`
  margin-bottom: 25px;
  position: relative;
`;

const InputWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 15px 20px;
  border: 2px solid #E9C9CE;
  border-radius: 12px;
  font-size: 16px;
  background: rgba(255, 255, 255, 0.9);
  transition: all 0.3s ease;
  outline: none;

  &:focus {
    border-color: #98012E;
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(152, 1, 46, 0.15);
  }

  &::placeholder {
    color: #686869;
    transition: all 0.3s ease;
  }

  &:focus::placeholder {
    transform: translateY(-20px);
    opacity: 0;
  }
`;

const FloatingLabel = styled.label`
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  color: #686869;
  font-size: 16px;
  pointer-events: none;
  transition: all 0.3s ease;
  background: white;
  padding: 0 5px;

  ${FormInput}:focus + &,
  ${FormInput}:not(:placeholder-shown) + & {
    top: 0;
    font-size: 12px;
    color: #ffffff;
    font-weight: 600;
    background: transparent;
  }
`;

const LoginButton = styled(motion.button)<{ $loading: boolean }>`
  width: 100%;
  padding: 16px;
  background: rgba(255, 255, 255, 0.9);
  color: #98012E;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(152,1,46,0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &:disabled {
    pointer-events: none;
  }

  ${props => props.$loading && css`
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 20px;
      height: 20px;
      margin: -10px 0 0 -10px;
      border: 2px solid transparent;
      border-top: 2px solid #98012E;
      border-radius: 50%;
      animation: ${spin} 1s linear infinite;
    }
  `}
`;

const FormFooter = styled(motion.div)`
  text-align: center;
  margin-top: 25px;
`;

const FooterLink = styled.a`
  color: #98012E;
  text-decoration: none;
  font-size: 14px;
  transition: all 0.3s ease;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: #98012E;
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }

  &:hover {
    color: #CA7C8E;
  }
`;

const ErrorMessage = styled(motion.div)`
  color: #ffffff;
  text-align: center;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.1);
  padding: 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  margin-bottom: 15px;
`;

export default LoginForm;
